import { Link, type LinkProps } from '@tanstack/react-router'

import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

const ROW = 'block border-b border-border-subtle py-3 text-left last:border-b-0'

// `rail-row-wash` is the rail's one row hover (globals.css): the same wash and
// bleed `PopularStockRowItem` draws, so a news row and a stock row cannot
// drift apart. The group names the row for its title's underline.
const ROW_LINK =
  'group/rail-row rail-row-wash cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring'

// Underlined on hover and focus, 3px off the text, as the mock draws it.
const TITLE_LINK =
  'underline-offset-3 group-hover/rail-row:underline group-focus-visible/rail-row:underline'

// The meta line steps up one ink under the wash: the subtle grey is 5:1 on the
// light canvas but 4.3:1 once the wash darkens it, so the hovered row keeps its
// 12px line readable.
const META_LINK =
  'transition-colors duration-fast group-hover/rail-row:text-muted-foreground group-focus-visible/rail-row:text-muted-foreground'

interface RailNewsRowProps {
  /** Joined with ", "; a missing part is left out rather than printed blank. */
  meta: ReadonlyArray<string | undefined>
  title: string
  /** Omitted for a note with no page to open: the row then draws as plain text. */
  link?: LinkProps
}

/**
 * The one headline row every right-rail news list draws: a 12/400 meta line
 * over a 14/400 title, hairline-separated. A row that links takes the rail's
 * hover wash (`rail-row-wash`) and underlines its title; one that does not
 * stays still. Market news and the Stock rail's company notices share it so
 * the rail cannot drift into two weights again.
 *
 * `line-clamp-3` is a safety cap, not a promise every title wraps that far.
 */
export default function RailNewsRow({ meta, title, link }: RailNewsRowProps) {
  const body = (
    <>
      <p
        className={cn('text-body-xs text-muted-foreground-subtle', link !== undefined && META_LINK)}
      >
        {meta.filter((part) => part !== undefined && part !== '').join(', ')}
      </p>
      <p
        className={cn(
          'mt-1 line-clamp-3 text-body-m text-foreground',
          link !== undefined && TITLE_LINK,
        )}
      >
        {title}
      </p>
    </>
  )

  if (link !== undefined) {
    return (
      <Link {...link} className={cn(ROW, ROW_LINK)}>
        {body}
      </Link>
    )
  }

  return <div className={ROW}>{body}</div>
}

/**
 * One `RailNewsRow`'s loading shape: a meta line over a two-line title at
 * `py-3`, ≈80px, the live design's own figure. A row, not a list, so a rail
 * list can hand it to `RailRows` and have it trimmed like the rows it stands
 * in for.
 */
export function RailNewsRowSkeleton() {
  return (
    <div className="border-b border-border-subtle py-3 last:border-b-0">
      <Skeleton className="h-3 w-24 rounded-xs" />
      <Skeleton className="mt-1 h-4 w-full rounded-xs" />
      <Skeleton className="mt-1 h-4 w-2/3 rounded-xs" />
    </div>
  )
}
